/**
 * FERP Portal - Tenant Subgraph Configuration Hook
 *
 * DATA-DRIVEN architecture hook that fetches ALL configuration for a tenant:
 * - Routes (for React Router)
 * - Navigation items (for Mantine)
 * - Entity types (for form generation)
 * - Permission scopes (for RBAC)
 *
 * FERP Principles:
 * - Data-driven: NO hardcoded frontend configuration
 * - Repository pattern: All access through GraphQL
 * - Life-critical: Proper tenant isolation
 *
 * Usage:
 * ```tsx
 * const { config, loading, error } = useTenantSubgraphConfig(tenantId);
 *
 * // Dynamic routes
 * config.allRoutes.map(route => <Route path={route.path} ... />)
 *
 * // Dynamic navigation
 * config.allNavItems.map(item => <NavLink to={item.path} ... />)
 *
 * // Dynamic forms
 * config.allEntityTypes.map(entity => <EntityForm schema={entity} ... />)
 * ```
 */

import { useMemo, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- This is a DATA-LAYER hook, allowed to use Apollo Client
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  useCollisionErrorHandler,
  type ParsedCollisionError,
  NAMESPACE_CONVENTIONS,
} from '@/utils/subgraph-error-handler';

// DRR-048: Widget registry integration for subgraph-driven composition
import {
  getWidget,
  getWidgetsByCategory,
  type WidgetCategory,
  type WidgetComponent,
  type WidgetType,
} from '@ferp/workflow-forms';

// Re-export for consumers
export type { ParsedCollisionError };
export { NAMESPACE_CONVENTIONS };

// ============================================================================
// DRR-048: Subgraph to Widget Category Mapping
// ============================================================================

/**
 * Maps subgraph IDs to widget categories.
 * This enables loading only widgets relevant to enabled subgraphs.
 *
 * GOVERNANCE RULE: When adding a new subgraph, update this mapping.
 */
const SUBGRAPH_TO_WIDGET_CATEGORY: Record<string, WidgetCategory | null> = {
  'ferp-core': 'core',
  'fhir': 'fhir',
  'payroll': 'payroll',
  'accounting': 'accounting',
  'erp': 'inventory',           // ERP includes inventory management
  'edi': null,                  // EDI uses core widgets only
  'website': null,              // Website uses core widgets only
  'legal': null,                // Legal uses core widgets only (future expansion)
  'manufacturing': 'inventory', // Manufacturing uses inventory widgets
  'government': null,           // Government uses core widgets only
};

/**
 * Resolve an array of widget ID strings into SubgraphWidgetInfo objects.
 * Filters out any IDs that do not exist in the registry.
 */
function resolveWidgetIds(widgetIds: string[]): SubgraphWidgetInfo[] {
  const results: SubgraphWidgetInfo[] = [];
  for (const widgetId of widgetIds) {
    const result = getWidget(widgetId);
    if (result.found && result.component) {
      results.push({
        type: widgetId as WidgetType,
        component: result.component,
      });
    }
  }
  return results;
}

/**
 * Widget info for subgraph filtering
 */
export interface SubgraphWidgetInfo {
  type: WidgetType;
  component: WidgetComponent;
}

// ============================================================================
// GraphQL Query
// ============================================================================

const GET_TENANT_SUBGRAPH_CONFIG = gql`
  query GetTenantSubgraphConfig($tenantId: String!) {
    getTenantSubgraphConfig(tenantId: $tenantId) {
      tenantId
      enabledSubgraphIds
      hasLifeCritical

      allRoutes {
        path
        component
        layer
        title
        requiredPermissions
        requiresLifeCriticalAuth
      }

      allNavItems {
        id
        label
        path
        icon
        layer
        sortOrder
        parentId
        badge
      }

      allEntityTypes {
        name
        pluralName
        description
        graphqlType
        displayField
        identifierFields
        formSchemaId
        listSchemaId
        permissions {
          create
          read
          update
          delete
          list
        }
        isLifeCritical
        complianceFrameworks
      }

      allPermissionScopes {
        scope
        description
        resourceType
        action
        isLifeCritical
      }

      subgraphs {
        subgraphId
        displayName
        description
        icon
        color
        isLifeCritical
        complianceFrameworks
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type ViewLayer = 'User' | 'Admin' | 'Architect';

export interface SubgraphRoute {
  path: string;
  component: string;
  layer: ViewLayer;
  title: string;
  requiredPermissions: string[];
  requiresLifeCriticalAuth: boolean;
}

export interface SubgraphNavItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  layer: ViewLayer;
  sortOrder: number;
  parentId: string | null;
  badge: { type: string; value?: string; color: string } | null;
}

export interface EntityPermissions {
  create: string;
  read: string;
  update: string;
  delete: string;
  list: string;
}

export interface SubgraphEntityType {
  name: string;
  pluralName: string;
  description: string;
  graphqlType: string;
  displayField: string;
  identifierFields: string[];
  formSchemaId: string | null;
  listSchemaId: string | null;
  permissions: EntityPermissions;
  isLifeCritical: boolean;
  complianceFrameworks: string[];
  icon: string;
  /** Category ID for grouping (e.g., "general-ledger", "patient-admin") */
  category?: string | null;
  /** Tier: essential | standard | advanced | specialist */
  tier?: 'essential' | 'standard' | 'advanced' | 'specialist' | null;
  /** Subgraph-specific key-value metadata (dynamic columns) */
  metadata?: Record<string, unknown>;
  /**
   * DRR-318: How this entity type is created.
   * - 'direct': Can create via CRUD form (default, shows Create button)
   * - 'workflow': Created via workflow only (shows Start Workflow button)
   * - 'pipeline': Created via intake pipeline (shows no create button, read-only)
   */
  creationSource?: 'direct' | 'workflow' | 'pipeline';
  /**
   * DRR-318: Workflow ID to trigger when creationSource is 'workflow'.
   * E.g., 'wf-4-0-033' for admission workflow that creates CarePlan.
   */
  creationWorkflowId?: string | null;
}

/** Entity tier levels for progressive disclosure */
export type EntityTier = 'essential' | 'standard' | 'advanced' | 'specialist';

/** Entity category for grouping entity types in management UI */
export interface EntityCategory {
  id: string;
  displayName: string;
  description: string;
  icon: string;
  sortOrder: number;
}

/** Pattern-prefix rule for auto-categorizing entity types */
export interface CategoryRule {
  patternPrefix: string;
  category: string;
  tier: string;
}

/** Entity preset — a named bundle for progressive disclosure */
export interface EntityPreset {
  id: string;
  displayName: string;
  description: string;
  icon: string;
  industry: string;
  /** Entity codes in this preset (empty = ALL) */
  entityCodes: string[];
  isDefault: boolean;
  sortOrder: number;
}

export interface SubgraphPermissionScope {
  scope: string;
  description: string;
  resourceType: string;
  action: string;
  isLifeCritical: boolean;
}

export interface SubgraphInfo {
  subgraphId: string;
  displayName: string;
  description: string;
  icon: string;
  color: string;
  isLifeCritical: boolean;
  complianceFrameworks: string[];
  /** Widget IDs available for this subgraph (DRR-048) */
  widgetIds?: string[];
  /** Entity categories for grouping in management UI */
  entityCategories?: EntityCategory[];
  /** Category rules for auto-categorization */
  categoryRules?: CategoryRule[];
  /** Entity presets for progressive disclosure */
  entityPresets?: EntityPreset[];
}

export interface TenantSubgraphConfig {
  tenantId: string;
  enabledSubgraphIds: string[];
  hasLifeCritical: boolean;
  allRoutes: SubgraphRoute[];
  allNavItems: SubgraphNavItem[];
  allEntityTypes: SubgraphEntityType[];
  allPermissionScopes: SubgraphPermissionScope[];
  subgraphs: SubgraphInfo[];
  /** All widget IDs available to this tenant (DRR-048) */
  allWidgetIds?: string[];
}

/**
 * Extended subgraph info with entity types for subgraph pages.
 */
export interface SubgraphConfigWithEntities extends SubgraphInfo {
  entityTypes: SubgraphEntityType[];
}

export interface TenantSubgraphConfigContext {
  config: TenantSubgraphConfig | null;
  loading: boolean;
  error: Error | null;

  // Collision error handling - FERP Life-Critical
  collisionErrors: ParsedCollisionError[];
  hasCollisions: boolean;

  // Convenience methods
  getRoutesForLayer: (layer: ViewLayer) => SubgraphRoute[];
  getNavItemsForLayer: (layer: ViewLayer) => SubgraphNavItem[];
  hasPermission: (scope: string) => boolean;
  hasSubgraph: (subgraphId: string) => boolean;
  getEntityType: (name: string) => SubgraphEntityType | null;
  getSubgraphConfig: (subgraphId: string) => SubgraphConfigWithEntities | null;
  refetch: () => Promise<void>;

  // DRR-048: Widget registry integration
  /** Get widgets available for a specific subgraph */
  getWidgetsForSubgraph: (subgraphId: string) => SubgraphWidgetInfo[];
  /** Get all widgets available for enabled subgraphs */
  getAvailableWidgets: () => SubgraphWidgetInfo[];
}

// ============================================================================
// Subgraph Definitions (used by route/nav post-processing)
// ============================================================================

/**
 * Available subgraph definitions (industry-agnostic metadata)
 * widgetIds: Widget IDs available for each subgraph (DRR-048)
 */
const SUBGRAPH_DEFINITIONS: Record<string, SubgraphInfo> = {
  'ferp-core': { subgraphId: 'ferp-core', displayName: 'FERP Core', description: 'Core 25 primitives', icon: 'IconStack', color: 'violet', isLifeCritical: false, complianceFrameworks: ['SOC2'], widgetIds: ['TextInput', 'NumberInput', 'Select', 'MultiSelect', 'Checkbox', 'DateInput', 'DateTimeInput', 'TextArea', 'Switch', 'RadioGroup', 'Slider'] },
  'fhir': { subgraphId: 'fhir', displayName: 'Healthcare', description: 'Medical records via Medplum', icon: 'IconHeartbeat', color: 'blue', isLifeCritical: true, complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'], widgetIds: ['PatientSearch', 'MedicationSelector', 'DiagnosisCodeLookup', 'VitalSignsInput'] },
  'payroll': { subgraphId: 'payroll', displayName: 'Payroll & Workforce', description: 'Payroll processing via TimeTrex', icon: 'IconCash', color: 'yellow', isLifeCritical: false, complianceFrameworks: ['SOX', 'FLSA'], widgetIds: ['TimeEntryGrid', 'PayPeriodSelector', 'EmployeeSearch'] },
  'accounting': { subgraphId: 'accounting', displayName: 'Accounting', description: 'Financial reporting via OFBiz', icon: 'IconCalculator', color: 'green', isLifeCritical: false, complianceFrameworks: ['SOX', 'GAAP'], widgetIds: ['GlAccountPicker', 'JournalLinesEditor', 'CurrencyInput', 'FiscalPeriodSelector'] },
  'erp': { subgraphId: 'erp', displayName: 'Accounting', description: 'Financial reporting via OFBiz', icon: 'IconCalculator', color: 'green', isLifeCritical: false, complianceFrameworks: ['SOX', 'GAAP'], widgetIds: ['GlAccountPicker', 'JournalLinesEditor', 'CurrencyInput', 'FiscalPeriodSelector'] },
  'website': { subgraphId: 'website', displayName: 'Website', description: 'CMS management', icon: 'IconWorld', color: 'cyan', isLifeCritical: false, complianceFrameworks: [], widgetIds: [] },
  'legal': { subgraphId: 'legal', displayName: 'Legal', description: 'Matter management', icon: 'IconScale', color: 'grape', isLifeCritical: false, complianceFrameworks: ['ABA-Rules'], widgetIds: [] },
  'manufacturing': { subgraphId: 'manufacturing', displayName: 'Manufacturing', description: 'Production management', icon: 'IconBuildingFactory', color: 'orange', isLifeCritical: false, complianceFrameworks: ['ISO-9001'], widgetIds: ['InventoryLookup', 'BomEditor', 'WorkOrderTracker'] },
  'government': { subgraphId: 'government', displayName: 'Government', description: 'Public services', icon: 'IconBuilding', color: 'indigo', isLifeCritical: false, complianceFrameworks: ['FedRAMP'], widgetIds: [] },
  'workforce': { subgraphId: 'workforce', displayName: 'Workforce', description: 'People, time & payroll operations', icon: 'IconUsers', color: 'yellow', isLifeCritical: false, complianceFrameworks: ['SOX', 'FLSA', 'FMLA'], widgetIds: ['EmployeeSearch', 'TimeEntryGrid', 'PayPeriodSelector'] },
  'edi': { subgraphId: 'edi', displayName: 'EDI / Clearinghouse', description: 'Electronic claims and remittance', icon: 'IconFileInvoice', color: 'orange', isLifeCritical: true, complianceFrameworks: ['HIPAA', 'X12'], widgetIds: [] },
  'wearables': { subgraphId: 'wearables', displayName: 'Wearables', description: 'Smart glasses and IoT devices', icon: 'IconDeviceWatch', color: 'lime', isLifeCritical: true, complianceFrameworks: ['HIPAA'], widgetIds: [] },
};
/**
 * Subgraph alias map — resolves alternate names to canonical subgraph IDs.
 * Canonical ID = what the backend seeds into DDB (SubgraphMetadata.subgraph_id).
 * Used by getSubgraphConfig() and useSubgraphIdFromPath() to resolve URL prefixes to canonical IDs.
 */
const SUBGRAPH_ALIASES: Record<string, string> = {
  'erp': 'accounting',   // Backend seeds subgraph_id: "accounting"; 'erp' is an alternate name
  'ofbiz': 'accounting',  // OFBiz backend engine alias → canonical "accounting"
};

/**
 * Resolve a subgraph ID through the alias map. Returns the canonical ID.
 */
function resolveSubgraphId(subgraphId: string): string {
  return SUBGRAPH_ALIASES[subgraphId] ?? subgraphId;
}

/**
 * Map subgraph IDs to their registered component names in COMPONENT_MAP.
 * When a subgraph has a dedicated page component, use it; otherwise fall back
 * to the generic SubgraphPage placeholder.
 */
const SUBGRAPH_COMPONENT_MAP: Record<string, string> = {
  'fhir': 'SubgraphBrowserPage',
  'payroll': 'SubgraphBrowserPage',
  'accounting': 'SubgraphBrowserPage',
  'erp': 'SubgraphBrowserPage',
  'edi': 'SubgraphBrowserPage',
  'website': 'SubgraphBrowserPage',
  'wearables': 'SubgraphBrowserPage',
  'workforce': 'WorkforceDashboardPage',
};

/**
 * Entity type definitions per subgraph.
 * In production, these come from SubgraphMetadata via GraphQL.
 */
const SUBGRAPH_ENTITY_TYPES: Record<string, SubgraphEntityType[]> = {
  'payroll': [
    {
      name: 'TimeEntry',
      pluralName: 'Time Entries',
      description: 'Employee time tracking entries',
      graphqlType: 'TimeEntry',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-time-entry',
      listSchemaId: 'payroll-time-entry-list',
      permissions: { create: 'payroll:time_entry:create', read: 'payroll:time_entry:read', update: 'payroll:time_entry:update', delete: 'payroll:time_entry:delete', list: 'payroll:time_entry:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconClock',
    },
    {
      name: 'PayrollRun',
      pluralName: 'Payroll Runs',
      description: 'Payroll calculation and processing runs',
      graphqlType: 'PayrollRun',
      displayField: 'payPeriod',
      identifierFields: ['id', 'tenantId'],
      formSchemaId: 'payroll-run',
      listSchemaId: 'payroll-run-list',
      permissions: { create: 'payroll:payroll_run:create', read: 'payroll:payroll_run:read', update: 'payroll:payroll_run:update', delete: 'payroll:payroll_run:delete', list: 'payroll:payroll_run:list' },
      isLifeCritical: true,
      complianceFrameworks: ['SOX', 'FLSA'],
      icon: 'IconReportMoney',
    },
    {
      name: 'PayStub',
      pluralName: 'Pay Stubs',
      description: 'Employee pay stub records',
      graphqlType: 'PayStub',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId', 'payrollRunId'],
      formSchemaId: 'payroll-pay-stub',
      listSchemaId: 'payroll-pay-stub-list',
      permissions: { create: 'payroll:pay_stub:create', read: 'payroll:pay_stub:read', update: 'payroll:pay_stub:update', delete: 'payroll:pay_stub:delete', list: 'payroll:pay_stub:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX'],
      icon: 'IconReceipt',
    },
    {
      name: 'TaxFiling',
      pluralName: 'Tax Filings',
      description: 'Federal and state tax filing records',
      graphqlType: 'TaxFiling',
      displayField: 'filingType',
      identifierFields: ['id', 'tenantId', 'year'],
      formSchemaId: 'payroll-tax-filing',
      listSchemaId: 'payroll-tax-filing-list',
      permissions: { create: 'payroll:tax_filing:create', read: 'payroll:tax_filing:read', update: 'payroll:tax_filing:update', delete: 'payroll:tax_filing:delete', list: 'payroll:tax_filing:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'IRS'],
      icon: 'IconFileInvoice',
    },
    {
      name: 'Deduction',
      pluralName: 'Deductions',
      description: 'Pre-tax and post-tax deduction definitions',
      graphqlType: 'Deduction',
      displayField: 'name',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-deduction',
      listSchemaId: 'payroll-deduction-list',
      permissions: { create: 'payroll:deduction:create', read: 'payroll:deduction:read', update: 'payroll:deduction:update', delete: 'payroll:deduction:delete', list: 'payroll:deduction:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconMinus',
    },
    {
      name: 'PayrollException',
      pluralName: 'Exceptions',
      description: 'Payroll anomalies requiring review',
      graphqlType: 'PayrollException',
      displayField: 'description',
      identifierFields: ['id', 'payrollRunId'],
      formSchemaId: 'payroll-exception',
      listSchemaId: 'payroll-exception-list',
      permissions: { create: 'payroll:exception:create', read: 'payroll:exception:read', update: 'payroll:exception:update', delete: 'payroll:exception:delete', list: 'payroll:exception:list' },
      isLifeCritical: true,
      complianceFrameworks: ['SOX'],
      icon: 'IconAlertTriangle',
    },
    // ── Time Tracking ──────────────────────────────────────────────
    {
      name: 'Timesheet',
      pluralName: 'Timesheets',
      description: 'Employee timesheet summaries by pay period',
      graphqlType: 'PayrollTimesheet',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId', 'payPeriodId'],
      formSchemaId: 'payroll-timesheet',
      listSchemaId: 'payroll-timesheet-list',
      permissions: { create: 'payroll:timesheet:create', read: 'payroll:timesheet:read', update: 'payroll:timesheet:update', delete: 'payroll:timesheet:delete', list: 'payroll:timesheet:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconClipboardList',
    },
    {
      name: 'PunchRecord',
      pluralName: 'Punch Records',
      description: 'Clock in/out punch records',
      graphqlType: 'PayrollPunchRecord',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-punch-record',
      listSchemaId: 'payroll-punch-record-list',
      permissions: { create: 'payroll:punch:create', read: 'payroll:punch:read', update: 'payroll:punch:update', delete: 'payroll:punch:delete', list: 'payroll:punch:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconFingerprint',
    },
    // ── Scheduling ─────────────────────────────────────────────────
    {
      name: 'Shift',
      pluralName: 'Shifts',
      description: 'Scheduled shift assignments',
      graphqlType: 'PayrollShift',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-shift',
      listSchemaId: 'payroll-shift-list',
      permissions: { create: 'payroll:shift:create', read: 'payroll:shift:read', update: 'payroll:shift:update', delete: 'payroll:shift:delete', list: 'payroll:shift:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconCalendarEvent',
    },
    {
      name: 'Schedule',
      pluralName: 'Schedules',
      description: 'Work schedules for teams and locations',
      graphqlType: 'PayrollSchedule',
      displayField: 'name',
      identifierFields: ['id'],
      formSchemaId: 'payroll-schedule',
      listSchemaId: 'payroll-schedule-list',
      permissions: { create: 'payroll:schedule:create', read: 'payroll:schedule:read', update: 'payroll:schedule:update', delete: 'payroll:schedule:delete', list: 'payroll:schedule:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconCalendar',
    },
    {
      name: 'Overtime',
      pluralName: 'Overtime Records',
      description: 'Overtime hours and compliance tracking',
      graphqlType: 'PayrollOvertime',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-overtime',
      listSchemaId: 'payroll-overtime-list',
      permissions: { create: 'payroll:overtime:create', read: 'payroll:overtime:read', update: 'payroll:overtime:update', delete: 'payroll:overtime:delete', list: 'payroll:overtime:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FLSA'],
      icon: 'IconClockHour4',
    },
    // ── Benefits & Leave ───────────────────────────────────────────
    {
      name: 'Benefit',
      pluralName: 'Benefits',
      description: 'Employee benefit enrollments and plans',
      graphqlType: 'PayrollBenefit',
      displayField: 'name',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-benefit',
      listSchemaId: 'payroll-benefit-list',
      permissions: { create: 'payroll:benefit:create', read: 'payroll:benefit:read', update: 'payroll:benefit:update', delete: 'payroll:benefit:delete', list: 'payroll:benefit:list' },
      isLifeCritical: false,
      complianceFrameworks: ['ACA'],
      icon: 'IconHeartHandshake',
    },
    {
      name: 'LeaveRequest',
      pluralName: 'Leave Requests',
      description: 'Employee leave and time-off requests',
      graphqlType: 'PayrollLeaveRequest',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-leave-request',
      listSchemaId: 'payroll-leave-request-list',
      permissions: { create: 'payroll:leave:create', read: 'payroll:leave:read', update: 'payroll:leave:update', delete: 'payroll:leave:delete', list: 'payroll:leave:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FMLA'],
      icon: 'IconBeach',
    },
    {
      name: 'PTOBalance',
      pluralName: 'PTO Balances',
      description: 'Paid time off accrual balances',
      graphqlType: 'PayrollPTOAccrual',
      displayField: 'employeeName',
      identifierFields: ['id', 'employeeId'],
      formSchemaId: 'payroll-pto-accrual',
      listSchemaId: 'payroll-pto-accrual-list',
      permissions: { create: 'payroll:pto:create', read: 'payroll:pto:read', update: 'payroll:pto:update', delete: 'payroll:pto:delete', list: 'payroll:pto:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FMLA'],
      icon: 'IconSunHigh',
    },
    {
      name: 'LeavePolicy',
      pluralName: 'Leave Policies',
      description: 'Leave and PTO accrual policy definitions',
      graphqlType: 'PayrollLeavePolicy',
      displayField: 'name',
      identifierFields: ['id'],
      formSchemaId: 'payroll-leave-policy',
      listSchemaId: 'payroll-leave-policy-list',
      permissions: { create: 'payroll:leave_policy:create', read: 'payroll:leave_policy:read', update: 'payroll:leave_policy:update', delete: 'payroll:leave_policy:delete', list: 'payroll:leave_policy:list' },
      isLifeCritical: false,
      complianceFrameworks: ['FMLA'],
      icon: 'IconFileDescription',
    },
  ],
  'fhir': [
    {
      name: 'Patient',
      pluralName: 'Patients',
      description: 'Patient medical records',
      graphqlType: 'FhirPatient',
      displayField: 'name',
      identifierFields: ['id'],
      formSchemaId: 'fhir-patient',
      listSchemaId: 'fhir-patient-list',
      permissions: { create: 'fhir:patient:create', read: 'fhir:patient:read', update: 'fhir:patient:update', delete: 'fhir:patient:delete', list: 'fhir:patient:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconUser',
    },
    {
      name: 'Practitioner',
      pluralName: 'Practitioners',
      description: 'Healthcare practitioners',
      graphqlType: 'FhirPractitioner',
      displayField: 'name',
      identifierFields: ['id', 'npi'],
      formSchemaId: 'fhir-practitioner',
      listSchemaId: 'fhir-practitioner-list',
      permissions: { create: 'fhir:practitioner:create', read: 'fhir:practitioner:read', update: 'fhir:practitioner:update', delete: 'fhir:practitioner:delete', list: 'fhir:practitioner:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA'],
      icon: 'IconStethoscope',
    },
    {
      name: 'Claim',
      pluralName: 'Claims',
      description: 'Insurance and billing claims',
      graphqlType: 'FhirClaim',
      displayField: 'status',
      identifierFields: ['id'],
      formSchemaId: 'fhir-claim',
      listSchemaId: 'fhir-claim-list',
      permissions: { create: 'fhir:claim:create', read: 'fhir:claim:read', update: 'fhir:claim:update', delete: 'fhir:claim:delete', list: 'fhir:claim:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconFileInvoice',
    },
    {
      name: 'Observation',
      pluralName: 'Observations',
      description: 'Clinical observations and lab results',
      graphqlType: 'FhirObservation',
      displayField: 'code',
      identifierFields: ['id'],
      formSchemaId: 'fhir-observation',
      listSchemaId: 'fhir-observation-list',
      permissions: { create: 'fhir:observation:create', read: 'fhir:observation:read', update: 'fhir:observation:update', delete: 'fhir:observation:delete', list: 'fhir:observation:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconTestPipe',
      // DRR-318: Observations are recorded via task completion, not direct CRUD
      creationSource: 'pipeline',
    },
    {
      name: 'Medication',
      pluralName: 'Medications',
      description: 'Medication definitions and formulary',
      graphqlType: 'FhirMedication',
      displayField: 'code',
      identifierFields: ['id'],
      formSchemaId: 'fhir-medication',
      listSchemaId: 'fhir-medication-list',
      permissions: { create: 'fhir:medication:create', read: 'fhir:medication:read', update: 'fhir:medication:update', delete: 'fhir:medication:delete', list: 'fhir:medication:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconPill',
    },
    {
      name: 'Encounter',
      pluralName: 'Encounters',
      description: 'Patient care encounters and visits',
      graphqlType: 'FhirEncounter',
      displayField: 'status',
      identifierFields: ['id'],
      formSchemaId: 'fhir-encounter',
      listSchemaId: 'fhir-encounter-list',
      permissions: { create: 'fhir:encounter:create', read: 'fhir:encounter:read', update: 'fhir:encounter:update', delete: 'fhir:encounter:delete', list: 'fhir:encounter:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconStethoscope',
      // DRR-318: Encounters are created via admission workflow, not direct CRUD
      creationSource: 'workflow',
      creationWorkflowId: 'wf-4-0-033',
    },
    {
      name: 'CarePlan',
      pluralName: 'Care Plans',
      description: 'Patient care plans and service plans',
      graphqlType: 'FhirCarePlan',
      displayField: 'status',
      identifierFields: ['id'],
      formSchemaId: 'fhir-careplan',
      listSchemaId: 'fhir-careplan-list',
      permissions: { create: 'fhir:careplan:create', read: 'fhir:careplan:read', update: 'fhir:careplan:update', delete: 'fhir:careplan:delete', list: 'fhir:careplan:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconClipboardList',
      // DRR-318: Care plans are created via admission workflow, not direct CRUD
      creationSource: 'workflow',
      creationWorkflowId: 'wf-4-0-033',
    },
    {
      name: 'Condition',
      pluralName: 'Conditions',
      description: 'Patient diagnoses and conditions affecting care',
      graphqlType: 'FhirCondition',
      displayField: 'code',
      identifierFields: ['id'],
      formSchemaId: 'fhir-condition',
      listSchemaId: 'fhir-condition-list',
      permissions: { create: 'fhir:condition:create', read: 'fhir:condition:read', update: 'fhir:condition:update', delete: 'fhir:condition:delete', list: 'fhir:condition:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconHeartbeat',
    },
    {
      name: 'ServiceRequest',
      pluralName: 'Service Requests',
      description: 'Ordered services and activities for residents',
      graphqlType: 'FhirServiceRequest',
      displayField: 'code',
      identifierFields: ['id'],
      formSchemaId: 'fhir-servicerequest',
      listSchemaId: 'fhir-servicerequest-list',
      permissions: { create: 'fhir:servicerequest:create', read: 'fhir:servicerequest:read', update: 'fhir:servicerequest:update', delete: 'fhir:servicerequest:delete', list: 'fhir:servicerequest:list' },
      isLifeCritical: false,
      complianceFrameworks: ['HIPAA', 'HL7-FHIR-R4'],
      icon: 'IconChecklist',
    },
  ],
  'accounting': [
    // ── General Ledger ─────────────────────────────────────────────
    {
      name: 'GlAccount',
      pluralName: 'GL Accounts',
      description: 'General Ledger accounts (chart of accounts)',
      graphqlType: 'ErpGlAccount',
      displayField: 'accountName',
      identifierFields: ['glAccountId'],
      formSchemaId: 'erp-glaccount-form-v1',
      listSchemaId: 'erp-glaccount-list-v1',
      permissions: { create: 'accounting:glaccount:create', read: 'accounting:glaccount:read', update: 'accounting:glaccount:update', delete: 'accounting:glaccount:delete', list: 'accounting:glaccount:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'GAAP'],
      icon: 'IconListTree',
    },
    {
      name: 'JournalEntry',
      pluralName: 'Journal Entries',
      description: 'Double-entry accounting journal entries',
      graphqlType: 'JournalEntry',
      displayField: 'description',
      identifierFields: ['id', 'entryNumber'],
      formSchemaId: 'accounting-journal-entry',
      listSchemaId: 'accounting-journal-entry-list',
      permissions: { create: 'accounting:journal_entry:create', read: 'accounting:journal_entry:read', update: 'accounting:journal_entry:update', delete: 'accounting:journal_entry:delete', list: 'accounting:journal_entry:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'GAAP'],
      icon: 'IconBook',
    },
    // ── Accounts Payable / Receivable ──────────────────────────────
    {
      name: 'Invoice',
      pluralName: 'Invoices',
      description: 'Customer and vendor invoices',
      graphqlType: 'ErpInvoice',
      displayField: 'description',
      identifierFields: ['invoiceId'],
      formSchemaId: 'erp-invoice-form-v1',
      listSchemaId: 'erp-invoice-list-v1',
      permissions: { create: 'accounting:invoice:create', read: 'accounting:invoice:read', update: 'accounting:invoice:update', delete: 'accounting:invoice:delete', list: 'accounting:invoice:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'GAAP'],
      icon: 'IconFileInvoice',
    },
    {
      name: 'Payment',
      pluralName: 'Payments',
      description: 'Incoming and outgoing payments',
      graphqlType: 'ErpPayment',
      displayField: 'paymentRefNum',
      identifierFields: ['paymentId'],
      formSchemaId: 'erp-payment-form-v1',
      listSchemaId: 'erp-payment-list-v1',
      permissions: { create: 'accounting:payment:create', read: 'accounting:payment:read', update: 'accounting:payment:update', delete: 'accounting:payment:delete', list: 'accounting:payment:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'GAAP'],
      icon: 'IconCreditCard',
    },
    {
      name: 'BillingAccount',
      pluralName: 'Billing Accounts',
      description: 'Customer billing accounts',
      graphqlType: 'ErpBillingAccount',
      displayField: 'description',
      identifierFields: ['billingAccountId'],
      formSchemaId: 'erp-billingaccount-form-v1',
      listSchemaId: 'erp-billingaccount-list-v1',
      permissions: { create: 'accounting:billingaccount:create', read: 'accounting:billingaccount:read', update: 'accounting:billingaccount:update', delete: 'accounting:billingaccount:delete', list: 'accounting:billingaccount:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX'],
      icon: 'IconReceipt2',
    },
    // ── Orders & Purchasing ────────────────────────────────────────
    {
      name: 'Order',
      pluralName: 'Orders',
      description: 'Purchase and sales orders',
      graphqlType: 'ErpOrderHeader',
      displayField: 'orderName',
      identifierFields: ['orderId'],
      formSchemaId: 'erp-orderheader-form-v1',
      listSchemaId: 'erp-orderheader-list-v1',
      permissions: { create: 'accounting:orderheader:create', read: 'accounting:orderheader:read', update: 'accounting:orderheader:update', delete: 'accounting:orderheader:delete', list: 'accounting:orderheader:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOC2'],
      icon: 'IconShoppingCart',
    },
    {
      name: 'Vendor',
      pluralName: 'Vendors',
      description: 'Vendor and supplier records',
      graphqlType: 'ErpVendor',
      displayField: 'vendorName',
      identifierFields: ['vendorId'],
      formSchemaId: 'erp-vendor-form-v1',
      listSchemaId: 'erp-vendor-list-v1',
      permissions: { create: 'accounting:vendor:create', read: 'accounting:vendor:read', update: 'accounting:vendor:update', delete: 'accounting:vendor:delete', list: 'accounting:vendor:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX'],
      icon: 'IconBuildingStore',
    },
    // ── Financial Planning ─────────────────────────────────────────
    {
      name: 'Budget',
      pluralName: 'Budgets',
      description: 'Departmental and project budgets',
      graphqlType: 'ErpBudget',
      displayField: 'comments',
      identifierFields: ['budgetId'],
      formSchemaId: 'erp-budget-form-v1',
      listSchemaId: 'erp-budget-list-v1',
      permissions: { create: 'accounting:budget:create', read: 'accounting:budget:read', update: 'accounting:budget:update', delete: 'accounting:budget:delete', list: 'accounting:budget:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'GAAP'],
      icon: 'IconChartPie',
    },
    {
      name: 'FixedAsset',
      pluralName: 'Fixed Assets',
      description: 'Capital equipment and fixed assets',
      graphqlType: 'ErpFixedAsset',
      displayField: 'fixedAssetName',
      identifierFields: ['fixedAssetId'],
      formSchemaId: 'erp-fixedasset-form-v1',
      listSchemaId: 'erp-fixedasset-list-v1',
      permissions: { create: 'accounting:fixedasset:create', read: 'accounting:fixedasset:read', update: 'accounting:fixedasset:update', delete: 'accounting:fixedasset:delete', list: 'accounting:fixedasset:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX', 'GAAP'],
      icon: 'IconBuildingFactory',
    },
    // ── Tax ─────────────────────────────────────────────────────────
    {
      name: 'TaxAuthority',
      pluralName: 'Tax Authorities',
      description: 'Tax jurisdictions and authorities',
      graphqlType: 'ErpTaxAuthority',
      displayField: 'taxAuthPartyId',
      identifierFields: ['taxAuthGeoId', 'taxAuthPartyId'],
      formSchemaId: 'erp-taxauthority-form-v1',
      listSchemaId: 'erp-taxauthority-list-v1',
      permissions: { create: 'accounting:taxauthority:create', read: 'accounting:taxauthority:read', update: 'accounting:taxauthority:update', delete: 'accounting:taxauthority:delete', list: 'accounting:taxauthority:list' },
      isLifeCritical: false,
      complianceFrameworks: ['SOX'],
      icon: 'IconBuildingBank',
    },
    {
      name: 'Product',
      pluralName: 'Products',
      description: 'Products and services catalog',
      graphqlType: 'ErpProduct',
      displayField: 'productName',
      identifierFields: ['productId'],
      formSchemaId: 'erp-product-form-v1',
      listSchemaId: 'erp-product-list-v1',
      permissions: { create: 'accounting:product:create', read: 'accounting:product:read', update: 'accounting:product:update', delete: 'accounting:product:delete', list: 'accounting:product:list' },
      isLifeCritical: false,
      complianceFrameworks: [],
      icon: 'IconPackage',
    },
  ],
  'edi': [
    {
      name: 'EdiTransaction',
      pluralName: 'EDI Transactions',
      description: 'Individual EDI transaction (837P, 835, 270, etc.)',
      graphqlType: 'EdiTransaction',
      displayField: 'transactionSetId',
      identifierFields: ['id'],
      formSchemaId: 'edi-transaction-form-v1',
      listSchemaId: 'edi-transaction-list-v1',
      permissions: { create: 'edi:transaction:create', read: 'edi:transaction:read', update: 'edi:transaction:update', delete: 'edi:transaction:delete', list: 'edi:transaction:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'X12-5010'],
      icon: 'IconFileInvoice',
    },
    {
      name: 'EdiTradingPartner',
      pluralName: 'Trading Partners',
      description: 'Clearinghouse or payer connection configuration',
      graphqlType: 'EdiTradingPartner',
      displayField: 'name',
      identifierFields: ['id'],
      formSchemaId: 'edi-trading-partner-form-v1',
      listSchemaId: 'edi-trading-partner-list-v1',
      permissions: { create: 'edi:partner:create', read: 'edi:partner:read', update: 'edi:partner:update', delete: 'edi:partner:delete', list: 'edi:partner:list' },
      isLifeCritical: false,
      complianceFrameworks: ['HIPAA'],
      icon: 'IconBuildingStore',
    },
    {
      name: 'EdiAcknowledgment',
      pluralName: 'Acknowledgments',
      description: '997/999 functional acknowledgment tracking',
      graphqlType: 'EdiAcknowledgment',
      displayField: 'status',
      identifierFields: ['id'],
      formSchemaId: 'edi-acknowledgment-form-v1',
      listSchemaId: 'edi-acknowledgment-list-v1',
      permissions: { create: 'edi:acknowledgment:create', read: 'edi:acknowledgment:read', update: 'edi:acknowledgment:update', delete: 'edi:acknowledgment:delete', list: 'edi:acknowledgment:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'X12-5010'],
      icon: 'IconChecklist',
    },
    {
      name: 'EdiInterchange',
      pluralName: 'Interchanges',
      description: 'Batch envelope (ISA/IEA) containing functional groups',
      graphqlType: 'EdiInterchange',
      displayField: 'controlNumber',
      identifierFields: ['id'],
      formSchemaId: 'edi-interchange-form-v1',
      listSchemaId: 'edi-interchange-list-v1',
      permissions: { create: 'edi:interchange:create', read: 'edi:interchange:read', update: 'edi:interchange:update', delete: 'edi:interchange:delete', list: 'edi:interchange:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA', 'X12-5010'],
      icon: 'IconPackage',
    },
  ],
  'wearables': [
    {
      name: 'WearableBeacon',
      pluralName: 'Beacons',
      description: 'IoT beacon device for location and biometric tracking',
      graphqlType: 'WearableBeacon',
      displayField: 'name',
      identifierFields: ['id'],
      formSchemaId: 'wearables-beacon-form-v1',
      listSchemaId: 'wearables-beacon-list-v1',
      permissions: { create: 'wearables:beacon:create', read: 'wearables:beacon:read', update: 'wearables:beacon:update', delete: 'wearables:beacon:delete', list: 'wearables:beacon:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA'],
      icon: 'IconDeviceWatch',
    },
    {
      name: 'WearableReading',
      pluralName: 'Readings',
      description: 'Sensor reading from a wearable device',
      graphqlType: 'WearableReading',
      displayField: 'readingType',
      identifierFields: ['id'],
      formSchemaId: 'wearables-reading-form-v1',
      listSchemaId: 'wearables-reading-list-v1',
      permissions: { create: 'wearables:reading:create', read: 'wearables:reading:read', update: 'wearables:reading:update', delete: 'wearables:reading:delete', list: 'wearables:reading:list' },
      isLifeCritical: true,
      complianceFrameworks: ['HIPAA'],
      icon: 'IconHeartbeat',
    },
  ],
};
// ============================================================================
// Post-Processing: Group flat nav items into collapsible sections
// ============================================================================

/**
 * Group definitions for User, Admin, and Architect layers.
 * Maps known nav item IDs to their group parent.
 * Items not in any group keep their original parentId (subgraph items already grouped).
 */
// DRR-102: No hardcoded group fallbacks. Navigation groups come from backend
// Setting config (st-0-0) via GetSettingNavigation query. If the query fails,
// groupNavItems returns scope-filtered items as a flat list — fail-closed.
// Single source of truth: common/configs/seed/settings/healthcare/st-0-0

/** Check if a nav item ID matches any of the given patterns (segment-boundary aware).
 *  Pattern "reports" matches "ferp-core-reports" (at segment boundary)
 *  but NOT "erp-financial-reports" (mid-segment). */
function idMatchesPatterns(id: string, patterns: string[]): boolean {
  const lower = id.toLowerCase();
  return patterns.some(p => {
    const idx = lower.indexOf(p);
    if (idx === -1) return false;
    // Match only at start of string or after a '-' delimiter
    return idx === 0 || lower[idx - 1] === '-';
  });
}

/** Build grouped nav items from core items and group definitions */
function buildGroupedItems(
  coreItems: SubgraphNavItem[],
  groups: { id: string; label: string; icon: string; sortOrder: number; memberPatterns: string[] }[],
  layerLabel: string,
): { grouped: SubgraphNavItem[]; groupedIds: Set<string> } {
  const grouped: SubgraphNavItem[] = [];
  const groupedIds = new Set<string>();

  for (const group of groups) {
    // Skip items already claimed by a prior group — first match wins, prevents cross-pattern collision
    const children = coreItems.filter(item => !groupedIds.has(item.id) && idMatchesPatterns(item.id, group.memberPatterns));
    if (children.length === 0) continue;

    grouped.push({
      // @ts-expect-error Possibly undefined
      id: group.id, label: group.label, path: children[0].path,
      // @ts-expect-error Type assignment
      icon: group.icon, layer: layerLabel, sortOrder: group.sortOrder,
      parentId: null, badge: null,
    });
    for (const child of children) {
      grouped.push({ ...child, parentId: group.id });
      groupedIds.add(child.id);
    }
  }
  return { grouped, groupedIds };
}

/**
 * Rewrite a backend-seeded nav path (e.g., /fhir/patients) to the data-driven
 * SubgraphBrowserPage format (e.g., /admin/fhir?entity=Patient).
 * Matches the last path segment against SUBGRAPH_ENTITY_TYPES for the subgraph.
 */
function rewriteNavPathForBrowser(subgraphId: string, originalPath: string): string {
  const segments = originalPath.split('/').filter(Boolean);
  const slug = segments[segments.length - 1] ?? '';
  const entityTypes = SUBGRAPH_ENTITY_TYPES[subgraphId] ?? [];

  const match = entityTypes.find(
    // @ts-expect-error TS2550
    (et) => et.graphqlType.toLowerCase() === slug.replaceAll('-', '').toLowerCase()
      // @ts-expect-error TS2550
      || et.pluralName.toLowerCase().replaceAll(' ', '-') === slug.toLowerCase()
      || et.name.toLowerCase() === slug.replace(/s$/, '').toLowerCase(),
  );

  return match ? `/admin/${subgraphId}?entity=${match.graphqlType}` : `/admin/${subgraphId}`;
}

/**
 * Build grouped parent + children entries for a single subgraph prefix.
 * If the subgraph uses SubgraphBrowserPage, rewrites paths to data-driven format.
 */
function buildSubgraphGroup(
  prefix: string, children: SubgraphNavItem[], layerLabel: string,
): SubgraphNavItem[] {
  const def = SUBGRAPH_DEFINITIONS[prefix];
  if (!def) return children;

  const parentId = `nav-${prefix}`;
  const minSort = Math.min(...children.map(c => c.sortOrder));
  const usesBrowserPage = SUBGRAPH_COMPONENT_MAP[prefix] === 'SubgraphBrowserPage';
  // @ts-expect-error Possibly undefined
  const parentPath = usesBrowserPage ? `/admin/${prefix}` : children[0].path;

  const parent: SubgraphNavItem = {
    id: parentId, label: def.displayName, path: parentPath,
    // @ts-expect-error Type assignment
    icon: def.icon, layer: layerLabel, sortOrder: minSort - 1,
    parentId: null, badge: def.isLifeCritical ? { type: 'dot', color: 'red' } : null,
  };

  const groupedChildren = children.map((child) => ({
    ...child,
    path: usesBrowserPage ? rewriteNavPathForBrowser(prefix, child.path) : child.path,
    parentId,
  }));

  return [parent, ...groupedChildren];
}

/**
 * Group flat subgraph items under subgraph parent headers.
 * Detects subgraph prefix from item ID (e.g., "fhir" from "fhir-patients")
 * and creates a collapsible parent using SUBGRAPH_DEFINITIONS metadata.
 */
function groupSubgraphItems(items: SubgraphNavItem[], layerLabel: string): SubgraphNavItem[] {
  const byPrefix = new Map<string, SubgraphNavItem[]>();
  const passThrough: SubgraphNavItem[] = [];

  for (const item of items) {
    if (item.parentId) { passThrough.push(item); continue; }

    const dashIdx = item.id.indexOf('-');
    const prefix = dashIdx > 0 ? item.id.substring(0, dashIdx) : '';
    const def = prefix ? SUBGRAPH_DEFINITIONS[prefix] : undefined;

    if (def && prefix !== 'ferp-core') {
      if (!byPrefix.has(prefix)) byPrefix.set(prefix, []);
      byPrefix.get(prefix)!.push(item);
    } else {
      passThrough.push(item);
    }
  }

  const result: SubgraphNavItem[] = [];
  for (const [prefix, children] of byPrefix) {
    result.push(...buildSubgraphGroup(prefix, children, layerLabel));
  }
  result.push(...passThrough);
  return result;
}

/**
 * Backend navigation group shape (matches SettingNavigationGroup GraphQL type).
 * Used to pass setting-defined groups into groupNavItems().
 */
export interface SettingNavGroup {
  __typename?: 'SettingNavigationGroup';
  id: string;
  label: string;
  icon: string;
  layer: string;
  sortOrder: number;
  memberPatterns: string[];
  /** Whether this is a standalone top-level item (Dashboard, Calendar) vs a collapsible group */
  isStandalone?: boolean;
  /** Primary path for standalone items */
  primaryPath?: string | null;
  /** Mantine color for the group icon/badge (e.g., "blue", "teal", "violet") */
  color?: string | null;
  /** DRR-196: Scopes required to see this navigation group. Empty = visible to all. */
  requiredScopes?: string[];
  /** DRR-196: Item-level scope requirements. Maps pattern → required scopes. */
  memberItemScopes?: Record<string, unknown> | null;
  /** DRR-188: Template for context banner action label */
  actionLabelTemplate?: string | null;
}

/**
 * Post-process nav items: wrap flat items in collapsible group parents.
 * 1. Core items (ferp-core-*) → grouped into My Work, Operations, Monitoring, Governance
 * 2. ERP items (erp-*) → split into Accounting (daily) and ERP (advanced) groups
 * 3. Remaining subgraph items (fhir-*, payroll-*) → grouped under subgraph parent headers
 * Works on backend data.
 *
 * DRR-055: When settingGroups are provided from the backend Setting config,
 * uses those for grouping (no hardcoded fallback — fail-closed per DRR-102).
 * This enables ALL layers (including User) to have navigation groups.
 */
export function groupNavItems(
  items: SubgraphNavItem[],
  layer: string,
  settingGroups?: SettingNavGroup[],
): SubgraphNavItem[] {
  // Already grouped (nav items with nav-grp-* parents with parentId references) → skip
  if (items.some(i => i.parentId?.startsWith('nav-'))) {
    return items;
  }

  // DRR-055: Use backend-provided groups for this layer if available
  // Normalize to uppercase for case-insensitive comparison (YAML has uppercase, GraphQL may vary)
  const normalizedLayer = layer.toUpperCase();
  const layerGroups = settingGroups?.filter(g => g.layer.toUpperCase() === normalizedLayer);
  if (layerGroups && layerGroups.length > 0) {
    // Separate standalone items (Dashboard, Calendar) from regular groups
    const standaloneGroups = layerGroups.filter(g => g.isStandalone);
    const regularGroups = layerGroups.filter(g => !g.isStandalone);

    const asLocalGroups = regularGroups.map(g => ({
      id: g.id,
      label: g.label,
      icon: g.icon,
      sortOrder: g.sortOrder,
      memberPatterns: g.memberPatterns,
    }));
    const allPatterns = asLocalGroups.flatMap(g => g.memberPatterns);
    const layerLabel = layer === 'ADMIN' ? 'Admin' : layer === 'ARCHITECT' ? 'Architect' : 'User';

    // Convert standalone "groups" into top-level nav items (not collapsible groups)
    const standaloneItems: SubgraphNavItem[] = standaloneGroups.map(g => ({
      id: g.id,
      label: g.label,
      path: g.primaryPath || '/', // Use primaryPath from YAML
      icon: g.icon,
      layer: layerLabel,
      sortOrder: g.sortOrder,
      parentId: null,
      badge: null,
    }));

    // Match ALL items (any subgraph) against setting group patterns.
    // This allows cross-subgraph grouping: e.g., fhir-residents matches
    // the "Care Management" group's "residents" pattern.
    const matchedItems: SubgraphNavItem[] = [];
    const remaining: SubgraphNavItem[] = [];
    for (const item of items) {
      const matchesGroupPattern = !item.parentId && idMatchesPatterns(item.id, allPatterns);
      (matchesGroupPattern ? matchedItems : remaining).push(item);
    }

    const { grouped: settingGrouped, groupedIds } = buildGroupedItems(matchedItems, asLocalGroups, layerLabel);
    const ungroupedMatched = matchedItems.filter(item => !groupedIds.has(item.id));
    const subgraphGrouped = groupSubgraphItems(remaining, layerLabel);

    // Merge standalone items with grouped items, sort by sortOrder
    return [...standaloneItems, ...settingGrouped, ...ungroupedMatched, ...subgraphGrouped].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // DRR-102: Fail-closed — no hardcoded fallback groups.
  // If setting config navigation groups aren't loaded, return scope-filtered items
  // as a flat list. Backend allowedNavigation already handles scope filtering;
  // grouping is cosmetic only. Hardcoded groups are PERMISSIVE (show all admin
  // items) and undermine fail-closed authorization.
  //
  // Subgraph items (fhir-*, erp-*) still get grouped under subgraph parents
  // so cross-subgraph items remain navigable.
  const layerLabel = layer === 'USER' ? 'User' : layer === 'ADMIN' ? 'Admin' : 'Architect';
  const coreItems = items.filter(item => !item.parentId);
  const subgraphPrefixed = coreItems.filter(item => item.id.includes('-') && !item.id.startsWith('ferp-'));
  const subgraphGrouped = groupSubgraphItems(subgraphPrefixed, layerLabel);
  const nonSubgraph = coreItems.filter(item => !subgraphPrefixed.includes(item));
  const childItems = items.filter(item => item.parentId);

  return [...nonSubgraph, ...childItems, ...subgraphGrouped].sort((a, b) => a.sortOrder - b.sortOrder);
}
// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Primary hook for data-driven tenant configuration.
 *
 * Returns ALL routes, navigation, entity types, and permissions
 * based on the tenant's enabled subgraphs.
 *
 * Backend is the single source of truth -- no synthetic data fallbacks.
 */
/**
 * Inject frontend-only nav items that the backend doesn't know about.
 * These are developer/architect tools that only exist in the portal frontend.
 */
function ensureFrontendOnlyNavItems(existingItems: SubgraphNavItem[]): SubgraphNavItem[] {
  // Only items that don't need group parents (groups are created later by groupNavItems)
  // Task Templates is injected post-grouping in getNavItemsForLayer (needs group parent IDs)
  const frontendOnlyItems: SubgraphNavItem[] = [];

  const patched = [...existingItems];
  for (const item of frontendOnlyItems) {
    if (!patched.some(i => i.id === item.id)) {
      patched.push(item);
    }
  }
  return patched;
}

/**
 * Ensure /admin/{subgraphId} and /architect/{subgraphId} routes exist
 * for every subgraph that uses SubgraphBrowserPage.
 * Backend seeds old-style routes (/fhir/patients) but not /admin/fhir.
 */
function ensureBrowserPageRoutes(existingRoutes: SubgraphRoute[]): SubgraphRoute[] {
  const patched = [...existingRoutes];
  for (const [subgraphId, componentName] of Object.entries(SUBGRAPH_COMPONENT_MAP)) {
    if (componentName !== 'SubgraphBrowserPage') continue;
    const def = SUBGRAPH_DEFINITIONS[subgraphId];
    for (const [pathPrefix, layer] of [['/admin/', 'Admin'], ['/architect/', 'Architect']] as const) {
      const fullPath = `${pathPrefix}${subgraphId}`;
      if (!patched.some((r) => r.path === fullPath)) {
        patched.push({
          path: fullPath, component: 'SubgraphBrowserPage', layer: layer as ViewLayer,
          title: def?.displayName ?? subgraphId, requiredPermissions: [`${subgraphId}:read`],
          requiresLifeCriticalAuth: def?.isLifeCritical ?? false,
        });
      }
    }
  }
  return patched;
}

export function useTenantSubgraphConfig(tenantId: string | null): TenantSubgraphConfigContext {
  // Skip backend query when no tenant ID is available
  const skipQuery = !tenantId;

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch: refetchQuery,
  // DRR-313: Use cache-first — subgraph config is tenant-level and rarely changes.
  // Apollo's onResetStore clears cache on auth changes, so stale data is not a concern.
  } = useQuery<Record<string, unknown>>(GET_TENANT_SUBGRAPH_CONFIG, {
    variables: { tenantId: tenantId ?? '' },
    skip: skipQuery,
    fetchPolicy: 'cache-first',
  });

  // FERP Data-Driven: Backend is SINGLE SOURCE OF TRUTH -- no synthetic data fallbacks
  const backendData = data?.getTenantSubgraphConfig as TenantSubgraphConfig | undefined;
  const backendFailed = queryError != null || (!queryLoading && !backendData && tenantId);

  const config = useMemo((): TenantSubgraphConfig | null => {
    if (backendData) {
      return {
        ...backendData,
        allRoutes: ensureBrowserPageRoutes(backendData.allRoutes),
        allNavItems: ensureFrontendOnlyNavItems(backendData.allNavItems),
      };
    }

    // Still loading or error -- return null (error surfaced via error state)
    return null;
  }, [backendData]);

  // Get routes for a specific layer
  // Backend returns uppercase (USER, ADMIN, ARCHITECT), frontend uses capitalized (User, Admin, Architect)
  const getRoutesForLayer = useCallback(
    (layer: ViewLayer): SubgraphRoute[] => {
      if (!config) return [];
      const normalizedLayer = layer.toUpperCase();
      return config.allRoutes.filter((r) => (r.layer as string).toUpperCase() === normalizedLayer);
    },
    [config]
  );

  // Get nav items for a specific layer (sorted + grouped)
  // Backend returns uppercase (USER, ADMIN, ARCHITECT), frontend uses capitalized (User, Admin, Architect)
  // Post-processing: groups flat core items into collapsible sections regardless of data source
  const getNavItemsForLayer = useCallback(
    (layer: ViewLayer): SubgraphNavItem[] => {
      if (!config) return [];
      const normalizedLayer = layer.toUpperCase();
      const items = config.allNavItems
        .filter((n) => (n.layer as string).toUpperCase() === normalizedLayer)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const grouped = groupNavItems(items, normalizedLayer);

      // Inject frontend-only nav items AFTER grouping.
      // These are items the backend doesn't seed but the portal needs.
      // Only inject if missing from backend data.
      if (normalizedLayer === 'ARCHITECT') {
        let buildGroup = grouped.find(i => (i.label === 'Build' || i.id === 'nav-grp-arch-composition') && !i.parentId);
        if (!buildGroup) {
          buildGroup = { id: 'nav-grp-arch-composition', label: 'Build', path: '/architect/primitives', icon: 'IconCode', layer: 'Architect', sortOrder: 100, parentId: null, badge: null };
          grouped.push(buildGroup);
        }
        if (!grouped.some(i => i.id === 'nav-explorer')) {
          grouped.push({ id: 'nav-explorer', label: 'Component Explorer', path: '/architect/explorer', icon: 'IconComponents', layer: 'Architect', sortOrder: 108, parentId: buildGroup.id, badge: null });
        }
        if (!grouped.some(i => i.id === 'nav-entity-types')) {
          grouped.push({ id: 'nav-entity-types', label: 'Entity Types', path: '/architect/entity-types', icon: 'IconStack2', layer: 'Architect', sortOrder: 109, parentId: buildGroup.id, badge: null });
        }
      }

      if (normalizedLayer === 'ADMIN') {
        let myWorkGroup = grouped.find(i => (i.label === 'My Work' || i.id === 'nav-grp-mywork') && !i.parentId);
        if (!myWorkGroup) {
          myWorkGroup = { id: 'nav-grp-mywork', label: 'My Work', path: '/ferp-core/dashboard', icon: 'IconClipboardList', layer: 'Admin', sortOrder: 50, parentId: null, badge: null };
          grouped.push(myWorkGroup);
        }

        let operationsGroup = grouped.find(i => (i.label === 'Operations' || i.id === 'nav-grp-operations') && !i.parentId);
        if (!operationsGroup) {
          operationsGroup = { id: 'nav-grp-operations', label: 'Operations', path: '/ferp-core/teams-admin', icon: 'IconTool', layer: 'Admin', sortOrder: 90, parentId: null, badge: null };
          grouped.push(operationsGroup);
        }
        if (!grouped.some(i => i.id === 'nav-admin-entity-types')) {
          grouped.push({ id: 'nav-admin-entity-types', label: 'Entity Types', path: '/admin/entity-types', icon: 'IconStack2', layer: 'Admin', sortOrder: 95, parentId: operationsGroup.id, badge: null });
        }
      }

      return grouped;
    },
    [config]
  );

  // Check if tenant has a permission scope
  const hasPermission = useCallback(
    (scope: string): boolean => {
      if (!config) return false;
      return config.allPermissionScopes.some((s) => s.scope === scope);
    },
    [config]
  );

  // Check if tenant has a subgraph enabled
  const hasSubgraph = useCallback(
    (subgraphId: string): boolean => {
      if (!config) return false;
      return config.enabledSubgraphIds.includes(subgraphId);
    },
    [config]
  );

  // Get entity type by name
  const getEntityType = useCallback(
    (name: string): SubgraphEntityType | null => {
      if (!config) return null;
      return config.allEntityTypes.find((e) => e.name === name) ?? null;
    },
    [config]
  );

  // Get subgraph config with its entity types
  // Supports alias resolution: 'erp' → 'accounting', 'ofbiz' → 'accounting' (via SUBGRAPH_ALIASES)
  const getSubgraphConfig = useCallback(
    (subgraphId: string): SubgraphConfigWithEntities | null => {
      if (!config) return null;

      // Resolve aliases: 'erp' → 'accounting', 'ofbiz' → 'accounting'
      const canonicalId = resolveSubgraphId(subgraphId);

      const subgraph = config.subgraphs.find((s) => s.subgraphId === canonicalId);
      if (!subgraph) return null;

      // Find entity types that belong to this subgraph
      // Entity types are associated by subgraph via their permission prefix (e.g., "fhir:patient:read")
      // Build prefix list: canonical ID + original ID + ALL reverse aliases that point to this canonical
      // This handles erp ↔ accounting: alias 'erp'/'ofbiz' resolves to canonical 'accounting'
      const prefixes = [canonicalId.toLowerCase()];
      if (canonicalId !== subgraphId) prefixes.push(subgraphId.toLowerCase());
      for (const [alias, target] of Object.entries(SUBGRAPH_ALIASES)) {
        if (target === canonicalId && !prefixes.includes(alias.toLowerCase())) {
          prefixes.push(alias.toLowerCase());
        }
      }
      let entityTypes = config.allEntityTypes.filter((e) => {
        const permissions = Object.values(e.permissions);
        return permissions.some((p) =>
          prefixes.some((prefix) => p.startsWith(prefix + ':'))
        );
      });

      // Fallback: if backend hasn't seeded entity types yet, use frontend definitions
      // Check canonical ID, original ID, and all aliases
      if (entityTypes.length === 0) {
        entityTypes = SUBGRAPH_ENTITY_TYPES[canonicalId]
          ?? SUBGRAPH_ENTITY_TYPES[subgraphId]
          ?? [];
        // Also try reverse aliases (e.g., 'erp' → 'accounting' means check SUBGRAPH_ENTITY_TYPES['erp'])
        if (entityTypes.length === 0) {
          for (const [alias, target] of Object.entries(SUBGRAPH_ALIASES)) {
            if (target === canonicalId && SUBGRAPH_ENTITY_TYPES[alias]) {
              entityTypes = SUBGRAPH_ENTITY_TYPES[alias];
              break;
            }
          }
        }
      }

      return {
        ...subgraph,
        entityTypes,
      };
    },
    [config]
  );

  // Refetch
  const refetch = useCallback(async () => {
    if (tenantId) {
      await refetchQuery();
    }
  }, [refetchQuery, tenantId]);

  // DRR-048: Get widgets for a specific subgraph
  // Uses backend widgetIds when available, falls back to static category mapping
  const getWidgetsForSubgraph = useCallback(
    (subgraphId: string): SubgraphWidgetInfo[] => {
      // Try to get widget IDs from backend config first
      if (config) {
        const subgraph = config.subgraphs.find(s => s.subgraphId === subgraphId);
        if (subgraph?.widgetIds?.length) {
          // Use backend-provided widget IDs
          const widgets: SubgraphWidgetInfo[] = [];
          for (const widgetId of subgraph.widgetIds) {
            const result = getWidget(widgetId);
            if (result.found && result.component) {
              widgets.push({
                type: widgetId as WidgetType,
                component: result.component,
              });
            }
          }
          return widgets;
        }
      }

      // Fallback: use static category mapping when backend hasn't provided widget IDs
      const category = SUBGRAPH_TO_WIDGET_CATEGORY[subgraphId];
      return resolveWidgetIds(getWidgetsByCategory(category ?? 'core'));
    },
    [config]
  );

  // DRR-048: Get all widgets available for enabled subgraphs
  // Uses backend allWidgetIds when available, falls back to category aggregation
  const getAvailableWidgets = useCallback(
    (): SubgraphWidgetInfo[] => {
      // Try to use backend-provided allWidgetIds first
      if (config?.allWidgetIds?.length) {
        const widgets: SubgraphWidgetInfo[] = resolveWidgetIds(config.allWidgetIds);
        // Always include core widgets even if not in backend list
        const coreWidgets = resolveWidgetIds(getWidgetsByCategory('core'));
        const widgetMap = new Map<string, SubgraphWidgetInfo>();
        coreWidgets.forEach(w => widgetMap.set(w.type, w));
        widgets.forEach(w => widgetMap.set(w.type, w));
        return Array.from(widgetMap.values());
      }

      // Fallback: aggregate widgets by category when backend hasn't provided widget IDs
      if (!config) return resolveWidgetIds(getWidgetsByCategory('core'));

      const widgets = new Map<string, SubgraphWidgetInfo>();

      // Add core widgets
      resolveWidgetIds(getWidgetsByCategory('core')).forEach(w => {
        widgets.set(w.type, w);
      });

      // Add widgets for each enabled subgraph
      config.enabledSubgraphIds.forEach(subgraphId => {
        const category = SUBGRAPH_TO_WIDGET_CATEGORY[subgraphId];
        if (category && category !== 'core') {
          resolveWidgetIds(getWidgetsByCategory(category)).forEach(w => {
            widgets.set(w.type, w);
          });
        }
      });

      return Array.from(widgets.values());
    },
    [config]
  );

  // Loading and error states - FAIL FAST, no synthetic data fallback
  const loading = queryLoading;
  const error = backendFailed ? queryError ?? null : null;

  // FERP Life-Critical: Handle collision errors with user-friendly messages
  // Auto-shows notifications for collision errors detected during tenant config load
  const { collisionErrors, hasCollisions } = useCollisionErrorHandler(
    error as Error | null,
    {
      autoShowNotifications: true,
      context: 'loading tenant configuration',
    }
  );

  return {
    config,
    loading,
    error: error as Error | null,
    collisionErrors,
    hasCollisions,
    getRoutesForLayer,
    getNavItemsForLayer,
    hasPermission,
    hasSubgraph,
    getEntityType,
    getSubgraphConfig,
    refetch,
    // DRR-048: Widget registry integration
    getWidgetsForSubgraph,
    getAvailableWidgets,
  };
}

export default useTenantSubgraphConfig;
